import React from 'react';
import type { ReactNode } from 'react';
import { isMobileOnly } from 'react-device-detect';
// TODO: use values from lodash since Object.values will return type of mixed
import { values } from 'lodash';
import Arrow, { ARROW_TYPES } from 'components/common/Arrow';
import { withTheme } from 'styled-components';
import type { ThemeProps, DefaultTheme } from 'styled-components';

/* Styled Components */
import {
  SelectBox,
  DropdownArrow,
  SelectBoxText,
  SelectBoxDropdown,
  SelectBoxDropdownItem,
} from 'styles/common/WishSelectInput';
import { RadioOption, Radio, RadioDot } from 'styles/common/WishRadioGroup';
import { FormElementTitle } from 'styles/common/FormElement';

/* Constants */
const MODE = { STANDARD: 'standard', RADIO: 'radio' } as const;

type PropsType = ThemeProps<DefaultTheme> & {
  title?: string;
  items: Array<any>;
  placeholder?: ReactNode;
  onChange: (index: number, _values: Array<any>) => void;
  styles?: {
    margin?: string;
    width?: string;
    height?: string;
    boxPadding?: string;
  };
  selectedIndex: number | null | undefined;
  dropdownItemStyle?: {
    padding?: string;
    justifyContent?: string;
  };
  isError?: boolean;
  disabled?: boolean | null | undefined;
  size?: 'extra-small' | 'small' | 'splash' | null;
  isExpandable?: boolean;
  isWarning?: boolean;
  mode?: string;
  // values of MODE
  noWrap?: boolean;
  parentHeight?: number;
  titleBold?: boolean;
  overrideTitleStyle?: React.CSSProperties;
  overrideStyle?: React.CSSProperties;
  overrideSelectBoxStyle?: React.CSSProperties;
  onClick?: (...args: any) => void;
  testIdSelector?: string; // data-testid for the dropdown selector
  testIdItems?: string; // data-testid for each of the items in the dropdown
};

type StateType = {
  isOpen: boolean;
  isOpenDown: boolean;
  height: number;
};

class WishSelectInput extends React.Component<PropsType, StateType> {
  elementRef: HTMLDivElement | null | undefined;

  itemRefs: { [item: number]: HTMLElement };

  selectBoxDropdownRef: HTMLElement | null | undefined;

  constructor(props: PropsType) {
    super(props);
    this.state = {
      isOpen: false,
      isOpenDown: true,
      height: 512,
    };
    this.elementRef = null;
    this.selectBoxDropdownRef = null;
    this.itemRefs = {};
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleDocumentClick);
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions.bind(this));
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleDocumentClick);
    window.removeEventListener(
      'resize',
      this.updateWindowDimensions.bind(this),
    );
  }

  handleDocumentClick = (event: MouseEvent) => {
    if (
      this.elementRef &&
      event.target instanceof Node &&
      !this.elementRef.contains(event.target)
    ) {
      // user clicked outside
      this.setState({ isOpen: false });
    }
  };

  onDropdownClick = () => {
    if (this.props.disabled) {
      return;
    }
    let openDown = true;
    if (this.elementRef) {
      const rect = this.elementRef.getBoundingClientRect();
      openDown = this.state.height - rect.top > 280;
    }
    this.setState(previousState => ({
      isOpen: !previousState.isOpen,
      isOpenDown: openDown,
    }));
  };

  onItemClick = (event: React.SyntheticEvent<HTMLElement>) => {
    this.props.onChange(
      parseInt(event.currentTarget.dataset.index as string, 10),
      this.props.items,
    );
  };

  setRef = (element: HTMLDivElement | null | undefined) => {
    this.elementRef = element;
  };

  setItemRef = (element: HTMLElement | null | undefined) => {
    if (element) {
      const index = parseInt(element.dataset.index as string, 10);
      this.itemRefs[index] = element;
    }
  };

  setSelectBoxRef = (element: HTMLElement | null | undefined) => {
    if (element) {
      this.selectBoxDropdownRef = element;
    }
  };

  updateWindowDimensions() {
    this.setState({ height: window.innerHeight });
  }

  render() {
    const { isOpen, isOpenDown } = this.state;
    const {
      items,
      placeholder,
      selectedIndex,
      styles = {},
      dropdownItemStyle = {},
      isError,
      disabled,
      size,
      title,
      isExpandable,
      isWarning,
      mode = MODE.STANDARD,
      noWrap = false,
      titleBold,
      overrideTitleStyle,
      overrideSelectBoxStyle,
      overrideStyle,
      theme,
      testIdSelector,
      testIdItems,
    } = this.props;

    /* Set max item as 1 on feed page for buy option in hovering details */
    const maxItems = isMobileOnly && this.state.height < 600 ? 5 : 6;

    const dropDownItems = items.map((item, index) => (
      <SelectBoxDropdownItem
        /* eslint-disable react/no-array-index-key */
        key={index}
        onClick={this.onItemClick}
        data-index={index}
        padding={dropdownItemStyle.padding}
        justifyContent={dropdownItemStyle.justifyContent}
        size={size}
        ref={this.setItemRef}
        data-testid={testIdItems}
      >
        {mode === MODE.RADIO && (
          <RadioOption padding="0 4px 0 0">
            <Radio selected={index === selectedIndex}>
              <RadioDot selected={index === selectedIndex} />
            </Radio>
          </RadioOption>
        )}
        {item}
      </SelectBoxDropdownItem>
    ));

    return (
      <>
        {Boolean(title) && (
          <FormElementTitle
            size={size}
            isErrorShown={isError}
            bold={titleBold}
            style={overrideTitleStyle}
          >
            {title}
          </FormElementTitle>
        )}
        <SelectBox
          ref={this.setRef}
          onClick={this.onDropdownClick}
          isError={isError}
          disabled={disabled}
          margin={styles.margin}
          width={styles.width}
          height={styles.height}
          isWarning={isWarning}
          style={overrideStyle}
          data-testid={testIdSelector}
        >
          <SelectBoxText size={size} boxPadding={styles.boxPadding}>
            {selectedIndex !== null &&
            selectedIndex !== undefined &&
            selectedIndex >= 0
              ? items[selectedIndex]
              : placeholder}
          </SelectBoxText>
          <DropdownArrow visible={isOpen ? 1 : 0}>
            <Arrow color={theme.Grey2} arrowType={ARROW_TYPES.TRIANGLE_ARROW} />
          </DropdownArrow>
          <SelectBoxDropdown
            ref={this.setSelectBoxRef}
            isVisible={isOpen}
            itemLength={items.length}
            isOpenDown={isOpenDown}
            size={size}
            isExpandable={isExpandable}
            noWrap={noWrap}
            maxItems={maxItems}
            selectBoxHeight={this.elementRef && this.elementRef.offsetHeight}
            dropdownHeight={values(this.itemRefs).reduce(
              (total, element) => total + (element ? element.offsetHeight : 0),
              0,
            )}
            style={overrideSelectBoxStyle}
          >
            {dropDownItems}
          </SelectBoxDropdown>
        </SelectBox>
      </>
    );
  }
}

export default withTheme(WishSelectInput);
